/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import { api } from "core/api";
import { Card, Input, SvgIcon, ControlledTable } from "core/components";
import { admin_material_returns_path, admin_material_return_path } from "routes";
import ControlledTableContext from "core/components/ControlledTableContext";

type MaterialReturnHistoryProps = {
  hasMaterialReturnHistory: boolean;
  hasWarehousesAccess: boolean;
};

const MaterialReturnHistory = ({ hasMaterialReturnHistory, hasWarehousesAccess }: MaterialReturnHistoryProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [materialReturns, setMaterialReturns] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: "code",
        Header: "Material Return",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        id: "project_code",
        Header: "Project",
        accessor: "project.code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Created By",
        accessor: "created_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Received By",
        accessor: "received_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Total Items",
        accessor: "total_items",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.material_return.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Material Return"
                data-bs-toggle="tooltip"
                onClick={() => {
                  window.open(admin_material_return_path(props.row.original.id));
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
            </div>
          );
        }
      }
    ],
    []
  );

  const getMaterialReturns = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(admin_material_returns_path({ history: true, page, per, filters: JSON.stringify(filters) }), { signal });
      setMaterialReturns(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No material return history.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Material Return Number" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Project" onChange={(e) => onChange("project_code", e.target.value)} />
        </Col>
        {hasWarehousesAccess && (
          <Col>
            <Input placeholder="Search for Warehouse" onChange={(e) => onChange("warehouse", e.target.value)} />
          </Col>
        )}
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={materialReturns}
              ref={table}
              isLoading={isLoading}
              fetchData={getMaterialReturns}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return hasMaterialReturnHistory ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Card.Body>{content()}</Card.Body>
    </React.StrictMode>
  );
};

export default MaterialReturnHistory;
